import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  deriveKeyFromPin,
  decrypt,
  zeroize,
  unwrapKeyWithPrf,
} from '@gonka-wallet/crypto';
import {
  openWalletDb,
  wipeAll,
  type EncryptedWalletBlob,
} from '@gonka-wallet/storage';
import { Button } from '@gonka-wallet/ui-tokens';
import { PinPad } from '../components/PinPad.js';
import { LogoTile } from '../components/LogoTile.js';
import { BiometricOfferModal } from '../components/BiometricOfferModal.js';
import { useSessionStore } from '../stores/sessionStore.js';
import { useWalletStore } from '../stores/walletStore.js';
import { hexToBytes } from '../lib/format.js';
import {
  authenticateBiometric,
  isWebAuthnAvailable,
} from '../lib/webauthn.js';

const MAX_ATTEMPTS = 10;

export function Unlock() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wallets = useWalletStore((s) => s.wallets);
  const activeId = useWalletStore((s) => s.activeId);
  const setWallets = useWalletStore((s) => s.setWallets);
  const setUnlocked = useSessionStore((s) => s.setUnlocked);
  const recordFailed = useSessionStore((s) => s.recordFailedAttempt);
  const sessionReset = useSessionStore((s) => s.reset);
  const failedAttempts = useSessionStore((s) => s.failedAttempts);
  const delayUntil = useSessionStore((s) => s.delayUntil);
  // SECURITY (F6): re-render every second so that the countdown message
  // and the PinPad disabled state update live, and so that when the
  // cooldown expires the UI reveals the pad without a full reload.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!delayUntil || delayUntil <= now) return;
    const interval = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(interval);
  }, [delayUntil, now]);
  const delayRemainingMs = useMemo(
    () => (delayUntil && delayUntil > now ? delayUntil - now : 0),
    [delayUntil, now],
  );
  const isLockedOut = delayRemainingMs > 0;
  const [error, setError] = useState<string | null>(null);
  const [shaking, setShaking] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [biometricBusy, setBiometricBusy] = useState(false);
  // After a successful PIN unlock we may pause on the unlock screen
  // to offer biometric enrollment. We hold the just-derived key here
  // so the user doesn't have to re-enter their PIN to enroll.
  const [pendingOffer, setPendingOffer] = useState<{
    wallet: EncryptedWalletBlob;
    derivedKey: Uint8Array;
  } | null>(null);
  // Once-per-mount guard so we don't re-fire the Face ID prompt in a
  // loop after the user cancels it.
  const autoFiredRef = useRef(false);

  const activeWallet =
    wallets.find((w) => w.id === activeId) ?? wallets[0];
  const biometricAvailable =
    !!activeWallet?.biometricWrap && isWebAuthnAvailable();

  async function wipeToRecovery() {
    const db = await openWalletDb();
    await wipeAll(db);
    setWallets([]);
    sessionReset();
    navigate('/wiped');
  }

  async function unlockWithBiometric() {
    const w = activeWallet;
    if (!w?.biometricWrap) return;
    setBiometricBusy(true);
    setError(null);
    let prfSecret: Uint8Array = new Uint8Array(0);
    let derivedKey: Uint8Array = new Uint8Array(0);
    let plaintext: Uint8Array = new Uint8Array(0);
    try {
      // 1. Face ID prompt → PRF secret.
      const prfSalt = hexToBytes(w.biometricWrap.prfSalt);
      const result = await authenticateBiometric(
        w.biometricWrap.credentialId,
        prfSalt,
      );
      prfSecret = result.prfSecret;

      // 2. Unwrap the seed-encryption key.
      const wrapped = hexToBytes(w.biometricWrap.wrappedKey);
      derivedKey = await unwrapKeyWithPrf(wrapped, prfSecret);

      // 3. Decrypt the seed exactly as the PIN path would.
      const nonce = hexToBytes(w.nonce);
      const ciphertext = hexToBytes(w.ciphertext);
      plaintext = decrypt(derivedKey, nonce, ciphertext);

      setUnlocked();
      navigate('/home');
    } catch (e) {
      const msg = (e as Error).message ?? '';
      if (msg.toLowerCase().includes('cancel') || msg.toLowerCase().includes('not allowed')) {
        // User dismissed Face ID — silent fall-through to PIN pad.
        setError(null);
      } else {
        setError(t('biometric.failed'));
      }
    } finally {
      zeroize(prfSecret, derivedKey, plaintext);
      setBiometricBusy(false);
    }
  }

  // Auto-trigger Face ID on mount if biometric is enabled. Fires once
  // per visit to the Unlock screen — if the user cancels we fall
  // through to the PIN pad and don't re-prompt in a loop.
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (!biometricAvailable) return;
    if (biometricBusy) return;
    autoFiredRef.current = true;
    void unlockWithBiometric();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biometricAvailable]);

  async function attempt(pin: string) {
    // SECURITY (F6): hard-enforce the progressive cooldown. Previously
    // `delayUntil` was computed by recordFailedAttempt() and shown as a
    // UI hint, but no code path blocked a subsequent PIN submission
    // while the delay was active — progressive lockout was effectively
    // cosmetic. Now we bail at the boundary and force the user to wait.
    const currentDelay = useSessionStore.getState().delayUntil;
    if (currentDelay && Date.now() < currentDelay) {
      const seconds = Math.ceil((currentDelay - Date.now()) / 1000);
      setError(t('unlock.waitSeconds', { seconds }));
      return;
    }

    const wallet = wallets.find((w) => w.id === activeId) ?? wallets[0];
    if (!wallet) {
      navigate('/welcome');
      return;
    }

    // Distinguish blob-corruption (hex parse) from wrong-PIN (decrypt auth
    // tag failure). Corruption routes directly to /wiped without consuming
    // the 10-attempt budget.
    let salt: Uint8Array;
    let nonce: Uint8Array;
    let ciphertext: Uint8Array;
    try {
      salt = hexToBytes(wallet.salt);
      nonce = hexToBytes(wallet.nonce);
      ciphertext = hexToBytes(wallet.ciphertext);
    } catch {
      await wipeToRecovery();
      return;
    }

    let key: Uint8Array = new Uint8Array(0);
    let plaintext: Uint8Array = new Uint8Array(0);
    let keepKey = false;
    try {
      key = await deriveKeyFromPin(pin, salt);
      plaintext = decrypt(key, nonce, ciphertext);
      setUnlocked();

      // Auto-prompt: if the device supports WebAuthn and this wallet
      // doesn't already have a biometricWrap, pause on the unlock
      // screen and offer enrollment. Shown on every unlock until the
      // user either enrolls or has already enrolled — "Not now" is
      // per-session only, not persistent, so we don't accidentally
      // hide it forever after one dismissal.
      const shouldOffer =
        isWebAuthnAvailable() && !wallet.biometricWrap;
      if (shouldOffer) {
        // Take a private copy of the key so the finally{} zeroize
        // doesn't wipe it out from under us.
        const keyCopy = new Uint8Array(key);
        setPendingOffer({ wallet, derivedKey: keyCopy });
        keepKey = true;
        return;
      }

      navigate('/home');
    } catch {
      const nextCount = failedAttempts + 1;
      if (nextCount >= MAX_ATTEMPTS) {
        await wipeToRecovery();
        return;
      }
      const delayMs = recordFailed();
      setError(
        delayMs > 0
          ? t('unlock.waitSeconds', { seconds: Math.ceil(delayMs / 1000) })
          : t('unlock.wrongPin'),
      );
      setShaking(true);
      setTimeout(() => setShaking(false), 420);
    } finally {
      if (!keepKey) zeroize(key, plaintext);
      else zeroize(plaintext);
    }
  }

  // While Face ID is in progress on first auto-fire, hide the PIN
  // pad entirely — the user shouldn't see noise behind the system
  // prompt. Once they cancel, the PIN pad reveals as a fallback.
  const showOnlyBiometric = biometricBusy && !error;

  return (
    <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-9 text-text-primary">
      <div className="flex flex-col items-center gap-3.5">
        <LogoTile />
        <div className="text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
            {t('app.name')}
          </div>
          <div className="mt-1 text-[18px] font-semibold tracking-[-0.4px] text-text-primary">
            {t('unlock.title')}
          </div>
        </div>
      </div>

      {showOnlyBiometric ? (
        <div className="mt-10 text-center text-[13px] text-text-secondary">
          {t('biometric.prompt')}
        </div>
      ) : (
        <>
          {biometricAvailable && !biometricBusy && (
            <button
              type="button"
              onClick={() => void unlockWithBiometric()}
              className="mt-2 py-3 text-center text-[14px] font-medium text-link"
            >
              {t('biometric.unlock')}
            </button>
          )}

          {isLockedOut ? (
            <div className="mt-2 text-center text-[11px] text-neg">
              {t('unlock.waitSeconds', {
                seconds: Math.ceil(delayRemainingMs / 1000),
              })}
            </div>
          ) : (
            error && (
              <div className="mt-2 text-center text-[11px] text-neg">{error}</div>
            )
          )}

          <div className="mt-auto">
            <PinPad onComplete={attempt} disabled={isLockedOut} shake={shaking} />
          </div>

          <button
            type="button"
            onClick={() => setShowForgot(true)}
            className="mt-2 text-center text-[12px] text-link"
          >
            {t('unlock.forgotPin')}
          </button>
        </>
      )}

      {showForgot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5">
          <div className="w-full max-w-sm rounded-2xl bg-surface-1 p-6">
            <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-neg">
              {t('unlock.destructiveLabel')}
            </div>
            <div className="mt-1 text-[17px] font-semibold text-text-primary">
              {t('unlock.forgotPin')}
            </div>
            <div className="mt-2 text-[13px] leading-relaxed text-text-secondary">
              {t('unlock.forgotPinExplanation')}
            </div>
            <div className="mt-5 flex flex-col gap-2">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  setShowForgot(false);
                  void wipeToRecovery();
                }}
              >
                <span className="text-neg">{t('wiped.importButton')}</span>
              </Button>
              <Button
                variant="tertiary"
                fullWidth
                onClick={() => setShowForgot(false)}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {pendingOffer && (
        <BiometricOfferModal
          wallet={pendingOffer.wallet}
          derivedKey={pendingOffer.derivedKey}
          onDone={() => {
            setPendingOffer(null);
            navigate('/home');
          }}
        />
      )}
    </div>
  );
}
